import { useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { connect } from "react-redux";
import {
  getEmployeeToVote,
  getMyVotes,
  getVotingBehaviors,
  getVotingRatings,
  submitVote,
} from "../../../store/voting/actions";
import defaultProfile from "../../assets/default-profile.jpg";
import AlertDialog from "../../profile/components/AlertDialog";
import { showError } from "../../toastify";
const MAX_COMMENT_LENGTH = 500;

const VotingForm = (props) => {
  const {
    setShowVotingForm,
    user,
    loading,
    voteBehaviors,
    voteRatings,
    myVotes,
    getVotingBehaviors,
    getVotingRatings,
    getMyVotes,
    getEmployeeToVote,
    submitVote,
  } = props;
  const [loadingVotes, setLoadingVotes] = useState(true);
  const [loadingRating, setLoadingRating] = useState(true);
  const [loadingBehavior, setLoadingBehavior] = useState(true);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wasVoted, setWasVoted] = useState(null);
  const [ratedBehaviors, setRatedBehaviors] = useState([]);
  const [employeeToVote, setEmployeeToVote] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (myVotes.length === 0) {
      getMyVotes(setLoadingVotes);
    } else setLoadingVotes(false);

    if (voteRatings.length === 0) {
      getVotingRatings(setLoadingRating);
    } else setLoadingRating(false);

    if (voteBehaviors.length === 0) {
      getVotingBehaviors(setLoadingBehavior);
    } else setLoadingBehavior(false);

    if (employeeToVote.length === 0) {
      getEmployeeToVote((status, data) => {
        setLoadingEmployees(status);
        setEmployeeToVote(data);
      });
    } else setLoadingEmployees(false);

    return () => {
      setLoadingVotes(true);
      setLoadingRating(true);
      setLoadingBehavior(true);
      setLoadingEmployees(true);
    };
  }, []);

  useEffect(() => {
    if (!!myVotes.length) {
      setWasVoted(
        !!myVotes.find((item) => item.fiscalYearId === user?.RBM_fiscalYear?.id)
      );
    } else setWasVoted(false);
  }, [myVotes]);

  const onClean = () => {
    // clean all states before close
    setSelectedEmployee(null);
    setEmployeeToVote([]);
    setRatedBehaviors([]);
    setWasVoted(null);

    setShowVotingForm();
  };

  const onClose = () => {
    // clean all states before close
    onClean();
    setShowVotingForm();
  };

  const handleRateChange = (behaviorId, rateId) => {
    if (!behaviorId || !rateId) return;

    const rating = voteRatings.find((item) => item.id === rateId);
    if (!rating) return;

    const index = ratedBehaviors.findIndex(
      (item) => item.behaviorId === behaviorId
    );
    if (index >= 0) {
      let _data = [...ratedBehaviors];
      _data[index].rateId = rateId;
      _data[index].rate = rating.rate;
      setRatedBehaviors([..._data]);
    } else {
      setRatedBehaviors([
        ...ratedBehaviors,
        { behaviorId, rateId, rate: rating.rate },
      ]);
    }
  };

  const handleSubmitVote = () => {
    // handle validation
    if (!selectedEmployee) {
      showError("Please selected employee you're voting for!");
      return;
    }

    if (!ratedBehaviors.length) {
      showError("Please rate the employee on different behaviors!");
      return;
    }

    if (ratedBehaviors.length !== voteBehaviors.length) {
      showError("All behavior must be rated!");
      return;
    }
    const formData = {
      employeePositionId: selectedEmployee?.id,
      groupDetailId: selectedEmployee?.groupDetailId,
      ratings: ratedBehaviors,
      comment,
      typeId: "D",
      groupType:1
    };
    submitVote(formData, (status, res) => {
      setIsSubmitting(status);
      if (!status && !!res) onClean();
    });
  };

  const isFormComplete =
    selectedEmployee && ratedBehaviors.length === voteBehaviors.length;

  return (
    <>
      <Dialog
        onClose={onClose}
        aria-labelledby="vote-dialog-title"
        open={true}
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: {
            borderRadius: "16px",
            padding: "20px",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">Voting Form:</Typography>
          <Box display="flex" alignItems="center" gap={2} mt={1}>
            <Chip label={user?.RBM_fiscalYear?.name} color="primary" />
            <Autocomplete
              fullWidth
              disablePortal
              size="small"
              value={selectedEmployee}
              options={employeeToVote}
              getOptionLabel={(option) => option.names}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <div {...props}>
                  <Avatar
                    style={{ border: "1px solid #d1cbcb" }}
                    alt={option.names}
                    className="mr-2"
                    src={
                      option.profileImage
                        ? `data:image/jpeg;base64,${option.profileImage}`
                        : defaultProfile
                    }
                  />
                  <span>
                    <span className="d-block text-primary">{option.names}</span>
                    <small className="d-block">
                      {!!option.isActing && (
                        <span className="text-primary font-weight-bold">
                          Ag.
                        </span>
                      )}{" "}
                      {option.positionName}
                    </small>
                    <small className="d-block text-uppercase mb-1">
                      {option.unitName}
                    </small>
                    <Divider />
                    <Divider />
                  </span>
                </div>
              )}
              onChange={(event, newValue) => {
                setSelectedEmployee(newValue);
                setRatedBehaviors([]);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Employee To Vote"
                  placeholder="Select Employee To Vote"
                />
              )}
              loading={loadingEmployees}
              loadingText="Loading employees..."
            />
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Behavior Rating:
          </Typography>

          <Grid container spacing={2} alignItems="start">
            {voteBehaviors.map((behavior) => {
              const rated = ratedBehaviors.find(
                (item) => item.behaviorId === behavior.id
              );
              return (
                <Grid item xs={12} sm={6} key={behavior.id}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Typography variant="body1" noWrap>
                      {behavior.name}
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel>Rate</InputLabel>
                      <Select
                        value={rated?.rateId || ""}
                        label="Rate"
                        onChange={(e) =>
                          handleRateChange(behavior.id, e.target.value)
                        }
                        disabled={!selectedEmployee}
                      >
                        {voteRatings.map((rating) => (
                          <MenuItem key={rating.id} value={rating.id}>
                            {rating.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Paper>
                </Grid>
              );
            })}
            {/* Comment Section */}
            {!!voteBehaviors.length && (
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Typography variant="body1" noWrap>
                    {/* {"behavior.name"} */}
                  </Typography>
                  <FormControl fullWidth size="small">
                    {/* <InputLabel>Rate</InputLabel> */}
                    <TextField
                      multiline
                      fullWidth
                      label="Feedback or Observations"
                      placeholder="Write your feedback or observations here"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      variant="outlined"
                      minRows={2}
                      maxRows={5}
                      inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
                    />
                    <Box display="flex" justifyContent="flex-end">
                      <Typography variant="caption" color="textSecondary">
                        <small>
                          {comment.length}/{MAX_COMMENT_LENGTH} characters
                        </small>
                      </Typography>
                    </Box>
                  </FormControl>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions className="d-flex justify-content-between mt-3">
          <Button
            variant="outlined"
            onClick={onClose}
            fullWidth
            sx={{ borderRadius: 2 }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setConfirmSubmit(true)}
            fullWidth
            sx={{ ml: 2, borderRadius: 2 }}
            disabled={!isFormComplete || isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {!!confirmSubmit && (
        <AlertDialog
          status={true}
          handleClose={() => setConfirmSubmit(false)}
          handleConfirm={() => handleSubmitVote()}
          title="Are you sure you want to submit your vote?"
          desc="Once votes are submitted, you cannot undo your action!"
          disabled={loading || loadingVotes || isSubmitting}
        />
      )}
    </>
  );
};

const mapStateToProps = ({
  loading,
  user,
  voteBehaviors,
  voteRatings,
  myVotes,
}) => {
  return {
    loading,
    user,
    voteBehaviors,
    voteRatings,
    myVotes,
  };
};

export default connect(mapStateToProps, {
  getVotingBehaviors,
  getVotingRatings,
  getMyVotes,
  getEmployeeToVote,
  submitVote,
})(VotingForm);
